import { getAgentById } from '@nemuru-eng/js-api-client';
import { useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { PAGE_AGENT_ALLOWED_ROLES } from '@/components/views/DynamicPageView/config/layoutScheme/constants/PageAgentAllowedRoles';
import { PAGE_CHANNEL_ALLOWED_ROLES } from '@/components/views/DynamicPageView/config/layoutScheme/constants/PageChannelAllowedRoles';
import { PAGE_MERCHANT_ALLOWED_ROLES } from '@/components/views/DynamicPageView/config/layoutScheme/constants/PageMerchantAllowedRoles';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { Participant, ParticipantRole } from '@/config/types';
import MerchantStructureEntity from '@/entities/MerchantStructureEntity';
import { useGetMerchantStructureById } from '@/hooks/requests/merchant/useGetMerchantStructureById';
import { useCache } from '@/hooks/requests/useCache';
import { useRoles } from '@/hooks/useRoles';

interface UseParticipants {
  merchant?: Participant;
  channel?: Participant;
  agent?: Participant;
}

interface UseParticipantsProps {
  agentId: string;
  merchantId: string;
}

export function useParticipants({ agentId, merchantId }: UseParticipantsProps): UseParticipants {
  const { roleExistsInProfile } = useRoles();

  const navigate = useNavigate();

  const { data: cachedAgent } = useCache(getAgentById, { agentId });
  const { merchantStructure: cachedMerchantStructure } = useGetMerchantStructureById(merchantId);

  const merchant = useMemo(() => {
    if (!cachedMerchantStructure) return;
    return {
      name: `${cachedMerchantStructure.merchant.businessName}`,
      role: ParticipantRole.MERCHANT,
      id: cachedMerchantStructure.merchant.id,
      ...(roleExistsInProfile(PAGE_MERCHANT_ALLOWED_ROLES) && {
        onClick: () => {
          navigate(
            generatePath(DYNAMIC_PAGE_PATHS[ParticipantRole.MERCHANT], {
              merchantId: cachedMerchantStructure.merchant.id,
            }),
          );
        },
        linkTo: generatePath(DYNAMIC_PAGE_PATHS[ParticipantRole.MERCHANT], {
          merchantId: cachedMerchantStructure.merchant.id,
        }),
      }),
    };
  }, [cachedMerchantStructure]);

  const channel = useMemo(() => {
    if (!cachedAgent || !cachedMerchantStructure) return;
    try {
      const channel = MerchantStructureEntity.create({
        merchantStructure: cachedMerchantStructure,
        agentId: cachedAgent.id,
      }).getChannel();

      if (!channel) throw new Error('Channel not found');

      return {
        name: `${channel.description}`,
        role: ParticipantRole.CHANNEL,
        id: channel.id,
        ...(roleExistsInProfile(PAGE_CHANNEL_ALLOWED_ROLES) && {
          onClick: () => {
            navigate(
              generatePath(DYNAMIC_PAGE_PATHS[ParticipantRole.CHANNEL], {
                merchantId: cachedMerchantStructure.merchant.id,
                channelId: channel.id,
              }),
            );
          },
          linkTo: generatePath(DYNAMIC_PAGE_PATHS[ParticipantRole.CHANNEL], {
            merchantId: cachedMerchantStructure.merchant.id,
            channelId: channel.id,
          }),
        }),
      };
    } catch (e) {}
  }, [cachedMerchantStructure, cachedAgent]);

  const agent = useMemo(() => {
    if (!cachedAgent || !cachedMerchantStructure) return;
    try {
      const channel = MerchantStructureEntity.create({
        merchantStructure: cachedMerchantStructure,
        agentId: cachedAgent.id,
      }).getChannel();

      if (!channel) throw new Error('Channel not found');

      return {
        name: `${cachedAgent.name}`,
        role: ParticipantRole.AGENT,
        id: cachedAgent.id,
        ...(roleExistsInProfile(PAGE_AGENT_ALLOWED_ROLES) && {
          onClick: () => {
            navigate(
              generatePath(DYNAMIC_PAGE_PATHS[ParticipantRole.AGENT], {
                merchantId: cachedMerchantStructure.merchant.id,
                channelId: channel.id,
                agentId: cachedAgent.id,
              }),
            );
          },
          linkTo: generatePath(DYNAMIC_PAGE_PATHS[ParticipantRole.AGENT], {
            merchantId: cachedMerchantStructure.merchant.id,
            channelId: channel.id,
            agentId: cachedAgent.id,
          }),
        }),
      };
    } catch (e) {}
  }, [cachedMerchantStructure, cachedAgent]);

  return { merchant, channel, agent };
}
